import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { documentsApi, vatApi, businessesApi, vendorsApi, auditApi, accountsApi, integrationsApi } from "@/lib/api";
import { useAuth, isMaster, canPublish, isBranchUser } from "@/lib/auth";
import StatusBadge from "@/components/dochub/StatusBadge";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Save, Send, ArrowLeft, Lock, RefreshCcw, ChevronLeft, ChevronRight, ClipboardList, X, RotateCw, RotateCcw, ZoomIn, ZoomOut, Maximize2, Trash2 } from "lucide-react";

const EMPTY_LI = { description: "", quantity: 1, unit_price: 0, line_total: 0 };

// Parse a numeric input that may contain simple arithmetic like "10+5" or
// "20*3-1" and return the evaluated number. Only + - * / and digits/decimals
// allowed — no `eval()` on arbitrary input. Fallback to raw parseFloat.
// Also accepts European decimal comma ("12,50") — normalises to "." first so
// ZA users typing R 12,50 don't get a NaN.
export function parseCalc(raw) {
  if (raw === "" || raw == null) return 0;
  const s = String(raw).trim().replace(/,/g, ".");
  if (/^[\d.]+$/.test(s)) return parseFloat(s) || 0;
  if (/^[-+]?[\d.+\-*/() ]+$/.test(s)) {
    try {
      // eslint-disable-next-line no-new-func
      const v = Function('"use strict";return (' + s + ")")();
      return isFinite(v) ? +v.toFixed(4) : 0;
    } catch { /* fall through */ }
  }
  return parseFloat(s) || 0;
}

// Numeric input that keeps the raw typed string while focused (so "12," on
// its way to "12,50" doesn't get eaten by controlled-state re-renders).
// Pushes the parsed numeric value up on every keystroke so live calculations
// still work; only re-syncs the display from the parent value when unfocused.
function NumInput({ value, onChange, disabled, testid, className }) {
  const [raw, setRaw] = useState(() => (value == null ? "" : String(value)));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setRaw(value == null ? "" : String(value));
  }, [value, focused]);
  return (
    <input
      type="text"
      inputMode="decimal"
      data-testid={testid}
      className={className}
      disabled={disabled}
      value={raw}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false);
        const n = parseCalc(e.target.value);
        onChange(n);
        setRaw(String(n));
      }}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(parseCalc(e.target.value));
      }}
    />
  );
}

function RetryButton({ id, onDone }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      data-testid="retry-processing-btn"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const res = await documentsApi.bulk([id], "retry");
          if (res.changed) toast.success("Re-queued — AI extraction restarted"); else toast.error("Could not retry this document");
          onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
        finally { setBusy(false); }
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-espresso text-canvas hover:opacity-90 disabled:opacity-50 shrink-0"
    >
      <RefreshCcw className="w-3.5 h-3.5" /> Retry processing
    </button>
  );
}

function UnassignedBanner({ doc, businesses, onAssigned }) {
  const [bizId, setBizId] = useState("");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    setBusy(true);
    try {
      await documentsApi.assign(doc.id, bizId);
      toast.success("Assigned — AI extraction started");
      onAssigned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-5 bg-gold-soft/50 border border-gold/30 rounded-xl flex flex-wrap items-end gap-4" data-testid="unassigned-banner">
      <div className="flex-1 min-w-[240px]">
        <div className="font-semibold text-espresso">Unassigned inbound document</div>
        <div className="text-sm text-espresso-ink mt-1" data-testid="unassigned-source">
          From {doc.source_email || "unknown sender"}{doc.source_subject ? <> · “{doc.source_subject}”</> : null}. Pick the business it belongs to — AI extraction runs once assigned.
        </div>
      </div>
      <select
        data-testid="assign-business-select"
        value={bizId}
        onChange={(e) => setBizId(e.target.value)}
        className="px-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus min-w-[200px]"
      >
        <option value="">Choose business…</option>
        {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <button
        data-testid="assign-business-btn"
        disabled={!bizId || busy}
        onClick={assign}
        className="px-4 py-2 rounded-lg bg-espresso text-canvas text-sm font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {busy ? "Assigning…" : "Assign & process"}
      </button>
    </div>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [ext, setExt] = useState(null);
  const [vats, setVats] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tick, setTick] = useState(0);
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [layout, setLayout] = useState(() => localStorage.getItem("dochub_doc_layout") || "horizontal");
  const [neighbours, setNeighbours] = useState({ prev: null, next: null });
  const [accounts, setAccounts] = useState([]);
  const [poOpen, setPoOpen] = useState(false);
  const [pos, setPos] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [poFilterVendor, setPoFilterVendor] = useState(true);
  const [previewRotation, setPreviewRotation] = useState(0); // 0/90/180/270 CSS rotation for the preview image
  const [previewZoom, setPreviewZoom] = useState(1); // 0.5 – 3x CSS scale for the preview image

  useEffect(() => {
    documentsApi.get(id).then((d) => {
      setDoc(d);
      setExt(d.extracted || { vendor_name: "", invoice_number: "", invoice_date: "", due_date: "", subtotal: 0, vat_amount: 0, total: 0, vat_rate: 0, tax_code_id: "", payment_method: "", currency: "ZAR", line_items: [] });
    });
    businessesApi.list().then(setBusinesses);
    auditApi.list({ document_id: id, limit: 50 }).then(setTimeline).catch(() => setTimeline([]));
  }, [id, tick]);

  useEffect(() => {
    if (doc?.business_id) {
      vatApi.list(doc.business_id).then(setVats);
      businessesApi.branches(doc.business_id).then(setBranches).catch(() => {});
      vendorsApi.list(doc.business_id).then((v) => { setVendors(v); setVendorsLoaded(true); }).catch(() => setVendorsLoaded(true));
      accountsApi.list(doc.business_id).then(setAccounts).catch(() => setAccounts([]));
      // Load neighbouring documents in the same business (sorted server-side newest-first)
      documentsApi.list({ business_id: doc.business_id }).then((list) => {
        const idx = list.findIndex((d) => d.id === doc.id);
        if (idx === -1) { setNeighbours({ prev: null, next: null }); return; }
        setNeighbours({ prev: list[idx - 1]?.id || null, next: list[idx + 1]?.id || null });
      }).catch(() => setNeighbours({ prev: null, next: null }));
    }
  }, [doc?.business_id, doc?.id]);

  // Poll for processing docs
  useEffect(() => {
    if (doc && (doc.status === "PROCESSING" || doc.status === "QUEUED")) {
      const t = setTimeout(() => setTick((v) => v + 1), 3000);
      return () => clearTimeout(t);
    }
  }, [doc]);

  // Apply business defaults ONCE per doc load. Two paths, in this order:
  //   1. If ext has NO tax code yet, pre-fill from biz.default_tax_code_id
  //      (backend now does this at OCR time too — this is the safety net for
  //      older docs uploaded before iter74).
  //   2. If ext DOES have a tax code (from OCR or vendor rule) but the line
  //      items don't, cascade the header code down so every line inherits it.
  //      Publish payloads to Xero/Zoho need per-line tax codes; without this,
  //      users had to manually re-pick the header code on every doc to
  //      trigger the update() cascade.
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    const biz = businesses.find((b) => b.id === doc?.business_id);
    if (!doc || !ext || !biz) return;
    // Iter88: also try to match the OCR-extracted vendor name to a vendor
    // rule and prefer its default_tax_code_id over the business-level one.
    // Without this the per-vendor default only fired when the backend OCR
    // path matched the vendor (which it doesn't when the user picks the
    // vendor manually AFTER upload, or when the OCR name variant didn't
    // hit `_find_vendor_match`).
    const nameKey = (ext.vendor_name || "").trim().toLowerCase();
    const matchedRule = nameKey
      ? (vendors || []).find((v) => {
          if ((v.name || "").trim().toLowerCase() === nameKey) return true;
          const aliases = (v.aliases || []).map((a) => (a || "").trim().toLowerCase());
          return aliases.includes(nameKey);
        })
      : null;
    const preferredTaxCode =
      (matchedRule && matchedRule.default_tax_code_id) ||
      biz.default_tax_code_id ||
      "";
    // Only take the ref lock once vendors have actually loaded — otherwise we
    // might race the vendors list and lock in the biz default before the
    // vendor rule arrives. `vendorsLoaded` distinguishes "still loading" from
    // "loaded but empty" (business with zero vendor rules) — iter89's earlier
    // `vendors.length === 0` guard caused the biz default to never apply for
    // docs whose business had no vendor rules yet (user-reported iter94).
    if (!vendorsLoaded || !vats.length) return;
    defaultsAppliedRef.current = true;
    // Lines inherit the doc's branch (e.g. branch-user uploads) when they have none.
    if (doc.branch_id && (ext.line_items || []).some((li) => !li.branch_id)) {
      setExt((e) => ({ ...e, line_items: (e.line_items || []).map((li) => li.branch_id ? li : { ...li, branch_id: doc.branch_id }) }));
    }
    if (!ext.tax_code_id && preferredTaxCode) {
      setTimeout(() => update("tax_code_id", preferredTaxCode), 0);
      return;
    }
    // Header code already set — make sure line items inherit it.
    const missing = (ext.line_items || []).some((li) => !li.tax_code_id);
    // Zero-rated header + all-zero-rated lines but VAT > 0 (OCR guessed 15%) → re-apply to clear VAT.
    const rateOf = (id) => { const v = vats.find((x) => x.id === id); return v ? Number(v.rate) || 0 : -1; };
    const zeroMismatch = ext.tax_code_id && rateOf(ext.tax_code_id) === 0 && Number(ext.vat_amount) > 0
      && (ext.line_items || []).every((li) => !li.tax_code_id || rateOf(li.tax_code_id) === 0);
    if (ext.tax_code_id && (missing || zeroMismatch)) {
      setTimeout(() => update("tax_code_id", ext.tax_code_id), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, ext, businesses, vendors, vendorsLoaded, vats]);

  const update = (field, val) => setExt((e) => {
    // Arithmetic in header numeric fields (net/vat/gross)
    const isNumeric = ["net_amount", "vat_amount", "gross_amount", "subtotal", "total"].includes(field);
    const raw = isNumeric ? parseCalc(val) : val;
    const next = { ...e, [field]: raw };
    // Header bidirectional calc — when the user edits net/vat/gross, derive
    // the missing one using the header tax rate. (Skip when user is picking
    // tax_code_id / expense_account — handled by the cascade below.)
    if (isNumeric) {
      const rate = (vats.find((v) => v.id === next.tax_code_id)?.rate) || 0;
      // No tax code selected OR zero-rated/exempt → don't auto-compute VAT,
      // preserve whatever the user typed for vat_amount (iter81 request).
      const noVat = rate === 0;
      const r2 = (n) => +Number(n || 0).toFixed(2);
      if (field === "net_amount" || field === "subtotal") {
        const net = raw;
        const vat = noVat ? (parseFloat(e.vat_amount) || 0) : r2(net * rate / 100);
        next.net_amount = net; next.subtotal = net;
        next.vat_amount = vat;
        next.gross_amount = r2(net + vat); next.total = next.gross_amount;
      } else if (field === "vat_amount") {
        next.vat_amount = raw;
        const n = parseFloat(next.net_amount ?? next.subtotal) || 0;
        next.gross_amount = r2(n + raw); next.total = next.gross_amount;
      } else if (field === "gross_amount" || field === "total") {
        const gross = raw;
        const net = noVat ? gross : r2(gross / (1 + rate / 100));
        next.gross_amount = gross; next.total = gross;
        next.net_amount = net; next.subtotal = net;
        // If noVat, preserve manually-entered vat; else derive from gross-net
        if (!noVat) next.vat_amount = r2(gross - net);
      }
    }
    // Cascade header selections into line items so users don't have to repeat
    // themselves. iter82: when the user changes the header tax code / expense
    // account, the cascade now OVERRIDES every line (not just empties). This
    // is what the user asked for: "The account, branch and vat selected
    // should update in the line items below" — picking a new header value is
    // a deliberate override signal.
    if (field === "tax_code_id" || field === "expense_account") {
      const rate = field === "tax_code_id" ? (vats.find((v) => v.id === val)?.rate || 0) : null;
      const r2 = (n) => +Number(n || 0).toFixed(2);
      next.line_items = (e.line_items || []).map((li) => {
        const patch = { ...li, [field]: val };
        if (field === "tax_code_id") {
          // Overwrite line VAT / total from the new rate. When user clears the
          // code (val === "") the rate is 0 → VAT collapses to 0 and total
          // matches net. Preserve OCR-extracted line VAT only when the code
          // stays at its original value (handled by the outer "no change"
          // guard) — a deliberate change is an override.
          const netAmt = parseFloat(li.net ?? li.line_total) || 0;
          const newVat = r2(netAmt * (rate || 0) / 100);
          patch.vat = newVat;
          patch.total = r2(netAmt + newVat);
        }
        return patch;
      });
      // Recompute header VAT / gross from the new rate too. iter82: this used
      // to only fire when the header VAT was empty — which meant OCR-set VAT
      // survived a manual tax-code change. Now: any deliberate tax code
      // change recomputes header VAT = net × rate (so picking "Zero Rate"
      // zeros it out, picking "Standard 15%" applies 15% straight away).
      if (field === "tax_code_id") {
        // 0% code: the printed gross is the truth, so net becomes gross (clears an OCR-guessed 15% split).
        if (!rate && parseFloat(e.vat_amount) > 0) {
          const g = parseFloat(e.gross_amount ?? e.total) || 0;
          next.net_amount = g; next.subtotal = g;
        }
        const netAmt = parseFloat(next.net_amount ?? next.subtotal) || 0;
        const newVat = r2(netAmt * (rate || 0) / 100);
        next.vat_amount = newVat;
        next.gross_amount = r2(netAmt + newVat);
        next.total = next.gross_amount;
      }
    }
    return next;
  });


  // Recompute derived fields on a single line item based on which field the
  // user just changed. Priority: preserve the user's edit; recompute the rest.
  //   * unit_price / quantity → net = qty × unit; vat = net × rate; total = net + vat
  //   * net                    → unit = net / qty (if qty); vat + total cascade
  //   * vat                    → total = net + vat
  //   * total                  → net = total / (1 + rate); vat = total - net; unit = net / qty
  const recomputeLine = (li, changed) => {
    const picked = vats.find((v) => v.id === li.tax_code_id);
    const rate = Number(picked?.rate) || 0;
    // Distinguish "no tax code picked" from "zero-rated / exempt code picked":
    //   * no code (li.tax_code_id empty)  → preserve manually-typed VAT
    //   * zero-rated code                 → force VAT = 0 (iter83 user complaint)
    const hasCode = !!li.tax_code_id;
    const isZeroRateCode = hasCode && rate === 0;
    const preserveVat = !hasCode; // only preserve VAT when NO code at all
    const r2 = (n) => +Number(n || 0).toFixed(2);
    const qty = parseFloat(li.quantity) || 0;
    let unit = parseFloat(li.unit_price) || 0;
    let net = parseFloat(li.net) || 0;
    let vat = parseFloat(li.vat) || 0;
    let total = parseFloat(li.total) || 0;
    if (isZeroRateCode) vat = 0; // hard-clear on any change when zero-rate is picked
    if (changed === "quantity" || changed === "unit_price") {
      net = r2(qty * unit);
      if (!preserveVat && !isZeroRateCode) vat = r2(net * rate / 100);
      total = r2(net + vat);
    } else if (changed === "net") {
      if (qty) unit = r2(net / qty);
      if (!preserveVat && !isZeroRateCode) vat = r2(net * rate / 100);
      total = r2(net + vat);
    } else if (changed === "vat") {
      total = r2(net + vat);
    } else if (changed === "total") {
      net = (rate && !isZeroRateCode) ? r2(total / (1 + rate / 100)) : total;
      vat = r2(total - net);
      if (qty) unit = r2(net / qty);
    } else if (changed === "tax_code_id") {
      if (isZeroRateCode) { vat = 0; total = net; }
      else if (rate > 0)  { vat = r2(net * rate / 100); total = r2(net + vat); }
      // else: no code — leave vat/total as user set them
    }
    return { ...li, quantity: qty, unit_price: unit, net, vat, total, line_total: net };
  };

  const updateLine = (i, f, v) => setExt((e) => {
    const items = [...(e.line_items || [])];
    // Allow arithmetic in numeric fields (e.g. "10+5", "20*3")
    const isNumeric = ["quantity", "unit_price", "net", "vat", "total"].includes(f);
    const parsed = isNumeric ? parseCalc(v) : v;
    items[i] = recomputeLine({ ...items[i], [f]: parsed }, f);
    return { ...e, line_items: items };
  });

  const addLine = () => setExt((e) => {
    const seed = {
      ...EMPTY_LI,
      tax_code_id: e.tax_code_id || "",
      expense_account: e.expense_account || "",
      branch_id: doc?.branch_id || "",
    };
    return { ...e, line_items: [...(e.line_items || []), seed] };
  });
  const removeLine = (i) => setExt((e) => ({ ...e, line_items: e.line_items.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await documentsApi.update(id, { extracted: ext });
      toast.success("Saved");
      setTick((v) => v + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const syncDraft = async () => {
    // Duplicate override — if this doc was flagged as a potential duplicate,
    // ask the operator to confirm rather than blocking or requiring a
    // separate "mark not a duplicate" click. Answering "Cancel" aborts the
    // publish. Answering "OK" flips the doc back to DRAFT server-side (so
    // future views don't keep nagging) and continues on to publish.
    if (doc.status === "DUPLICATE_SUSPECT") {
      const ok = window.confirm(
        "These two documents look like duplicates. Publish this one anyway?\n\n" +
        "Click OK if this is NOT a duplicate and you want to publish.\n" +
        "Click Cancel to review the potential duplicate first."
      );
      if (!ok) return;
      try {
        await documentsApi.markNotDup(id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not clear duplicate flag");
        return;
      }
    }
    setSyncing(true);
    try {
      await documentsApi.update(id, { extracted: ext });
      const res = await documentsApi.publish(id);
      if (res.payment_error) {
        // Bill posted to Xero/Zoho but the payment allocation failed — the doc
        // is still marked PUBLISHED because the bill itself is on the ledger.
        // Surface the payment problem so the user can fix it manually rather
        // than silently marking the bill as "Awaiting Payment" on Xero.
        toast.warning(
          `Published to ${res.provider} but payment allocation failed: ${res.payment_error}`,
          { duration: 10000 }
        );
      } else if (res.po_link_warning) {
        // Bill posted to Zoho but the matched PO couldn't be linked (usually
        // Zoho code 4197 — no common items between bill and PO). Bill is on
        // Zoho; PO needs manual close.
        toast.warning(res.po_link_warning, { duration: 12000 });
      } else if (res.payment_id) {
        toast.success(`Published to ${res.provider} · payment allocated`);
      } else {
        toast.success(`Published to ${res.provider} · ${res.external_id?.slice(0, 8)}…`);
      }
      // Auto-advance to the NEXT DRAFT doc — not just the neighbour in the
      // full-inbox order, which could easily be an already-published bill.
      // Scope to the current business (matches how the user was already
      // working) and pick the first draft that isn't the one we just posted.
      // If there are no more drafts, land back on the inbox.
      try {
        const drafts = await documentsApi.list({
          business_id: doc.business_id,
          status: "DRAFT_IN_DOCHUB",
          limit: 50,
        });
        const nextDraft = (drafts || []).find((d) => d.id !== id);
        if (nextDraft) {
          nav(`/documents/${nextDraft.id}`);
        } else {
          nav("/documents");
        }
      } catch {
        nav("/documents");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setSyncing(false); }
  };

  const markNotDup = async () => {
    try {
      await documentsApi.markNotDup(id);
      toast.success("Marked as not a duplicate");
      setTick((v) => v + 1);
    } catch (e) { toast.error("Failed"); }
  };

  const reassign = async (newBiz) => {
    if (!newBiz) return;
    try {
      await documentsApi.reassign(id, newBiz);
      toast.success("Reassigned");
      setTick((v) => v + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [extracting, setExtracting] = useState(false);
  const extractLines = async () => {
    setExtracting(true);
    try {
      // Save any in-flight header edits first so re-extraction doesn't
      // trample them (the endpoint only replaces line_items, but a stale
      // header on the server could confuse cascades on reload).
      await documentsApi.update(id, { extracted: ext });
      const res = await documentsApi.extractLineItems(id);
      toast.success(`Extracted ${res.count} line item${res.count === 1 ? "" : "s"}`);
      setTick((v) => v + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extract failed");
    } finally { setExtracting(false); }
  };

  if (!doc) return <div className="p-10 text-espresso-ink/60">Loading…</div>;

  const biz = businesses.find((b) => b.id === doc.business_id);
  // Branch users are strictly view-only: even on a DRAFT doc they can't edit
  // fields, add lines, or publish. Enforced client-side (UI) + backend-side
  // (PATCH /documents/:id returns 403).
  const isBranch = user?.role === "branch_user";
  const canEdit = !isBranch && !doc.locked && doc.status !== "PUBLISHED";
  const canPush = !isBranch && canPublish(user);
  const imgUrl = documentsApi.imageUrl(id);
  const isPdf = doc.content_type === "application/pdf";
  const matchedVendor = ext?.vendor_name ? vendors.find((v) => v.name?.toLowerCase() === ext.vendor_name.toLowerCase()) : null;
  const effectiveAccount = matchedVendor?.default_expense_account || (biz?.accounting === "xero" ? "400 (default)" : null);

  // Vendor rule auto-learn: detect diffs between the document's current picks and the matched vendor rule.
  // Resolves UUIDs to human-readable names (tax code name, branch name) so the banner is scannable —
  // showing "was Zero Rate → Standard Rate 15%" instead of "was (none) → 6cad3fc9-17c3-4b9d..."
  const labelForTax = (v) => vats.find((x) => x.id === v)?.name || (v ? `(unknown code)` : "(none)");
  const labelForBranch = (v) => branches.find((x) => x.id === v)?.name || (v ? `(unknown branch)` : "(none)");
  const labelPlain = (v) => v || "(none)";
  const vendorRuleDiff = (() => {
    if (!matchedVendor) return [];
    const diffs = [];
    if (ext?.tax_code_id && matchedVendor.default_tax_code_id !== ext.tax_code_id) diffs.push({ field: "Tax code", from: labelForTax(matchedVendor.default_tax_code_id), to: labelForTax(ext.tax_code_id) });
    if (doc.branch_id && matchedVendor.default_branch_id !== doc.branch_id) diffs.push({ field: "Branch", from: labelForBranch(matchedVendor.default_branch_id), to: labelForBranch(doc.branch_id) });
    if (ext?.payment_method && matchedVendor.default_payment_method !== ext.payment_method) diffs.push({ field: "Payment method", from: labelPlain(matchedVendor.default_payment_method), to: labelPlain(ext.payment_method) });
    if (doc.document_type && matchedVendor.default_document_type !== doc.document_type) diffs.push({ field: "Document type", from: labelPlain(matchedVendor.default_document_type), to: labelPlain(doc.document_type) });
    return diffs;
  })();

  const openPoModal = async () => {
    setPoOpen(true);
    setPoLoading(true);
    try {
      const vendorExtId = poFilterVendor ? matchedVendor?.external_id : null;
      const res = await integrationsApi.openPurchaseOrders(doc.business_id, vendorExtId);
      setPos(res.purchase_orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load POs");
      setPos([]);
    } finally { setPoLoading(false); }
  };

  const applyPO = async (po) => {
    // Map PO line items → local line_items schema. PO line totals are typically
    // net (excl VAT); we preserve any header tax code so VAT is applied on
    // publish (Zoho/Xero compute gross from unit_price × qty × tax_rate). Also
    // preserve the PO line's item_id so Zoho/Xero auto-links + closes the PO.
    const mapped = (po.line_items || []).map((li) => {
      const qty = parseFloat(li.quantity) || 0;
      const unit = parseFloat(li.unit_price) || 0;
      const total = parseFloat(li.total) || +(qty * unit).toFixed(2);
      const net = total; // PO totals are net-basis
      return {
        description: li.description || "",
        quantity: qty,
        unit_price: unit,
        line_total: net,
        net,
        vat: 0,
        total: net,
        expense_account: li.expense_account || "",
        tax_code_id: ext?.tax_code_id || "",
        branch_id: null,
        purchaseorder_item_id: li.line_item_id || li.purchaseorder_item_id || null,
        // Preserve the PO's linked item so publish satisfies Zoho's stock-account
        // requirement (error 13030) automatically — no manual re-link needed.
        item_id: li.item_id || null,
        item_code: li.item_code || null,
      };
    });
    if (mapped.length === 0) {
      toast.error("This PO has no line items — nothing to apply. Check the PO in your accounting system.");
      return;
    }
    const netSum = +mapped.reduce((s, l) => s + (l.net || 0), 0).toFixed(2);
    setExt((e) => ({
      ...e,
      line_items: mapped,
      net_amount: netSum,
      subtotal: netSum,
      gross_amount: +(netSum + (e?.vat_amount || 0)).toFixed(2),
      total: +(netSum + (e?.vat_amount || 0)).toFixed(2),
      invoice_number: e?.invoice_number || po.reference || po.number || "",
    }));
    setPoOpen(false);
    try {
      await documentsApi.update(id, { matched_po_id: po.id });
    } catch (e) {
      toast.warning("Line items applied — but couldn't save PO link. Publish may not auto-close the PO.");
    }
    toast.success(`Applied PO ${po.number || ""} — ${mapped.length} line item${mapped.length === 1 ? "" : "s"} · linked for auto-close. Review VAT and Save.`);
  };

  const updateVendorRule = async () => {
    try {
      // FIRST persist the current in-memory extraction — otherwise the tick
      // refresh below reloads the previously-saved doc from the backend and
      // silently reverts every edit the user just made in this session.
      await documentsApi.update(id, { extracted: ext });
      const res = await documentsApi.updateVendorRule(id);
      if (res.changed?.length) {
        toast.success(`Vendor rule updated: ${res.changed.join(", ")}`);
      } else {
        toast.info(res.message || "Vendor rule already matches");
      }
      // Refresh vendors so matchedVendor.defaults are current → banner disappears
      if (doc.business_id) {
        vendorsApi.list(doc.business_id).then(setVendors).catch(() => {});
      }
      setTick((v) => v + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="document-detail">
      <div className="flex items-center justify-between">
        <Link to="/documents" className="inline-flex items-center gap-2 text-sm text-espresso-ink hover:text-espresso" data-testid="back-to-inbox">
          <ArrowLeft className="w-4 h-4" /> Back to inbox
        </Link>
        <div className="flex items-center gap-2">
          <button
            data-testid="prev-doc-btn"
            disabled={!neighbours.prev}
            onClick={() => neighbours.prev && nav(`/documents/${neighbours.prev}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-espresso border border-canvas-border hover:bg-canvas-border/40 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Previous document (newer)"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>
          <button
            data-testid="next-doc-btn"
            disabled={!neighbours.next}
            onClick={() => neighbours.next && nav(`/documents/${neighbours.next}`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-espresso border border-canvas-border hover:bg-canvas-border/40 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Next document (older)"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
          {!isBranchUser(user) && !doc.locked && doc.status !== "PUBLISHED" && (
            <button
              data-testid="delete-doc-btn"
              onClick={async () => {
                if (!window.confirm("Delete this document? This cannot be undone.")) return;
                try {
                  const res = await documentsApi.bulk([id], "delete");
                  if (!res.changed) { toast.error("Document could not be deleted"); return; }
                  toast.success("Document deleted");
                  const go = neighbours.next || neighbours.prev;
                  nav(go ? `/documents/${go}` : "/documents");
                } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-status-duplicate border border-status-duplicate/30 hover:bg-status-duplicate/10"
              title="Delete document"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={doc.status} />
          {doc.locked && <span className="inline-flex items-center gap-1 text-xs text-espresso-ink/60"><Lock className="w-3 h-3" /> Locked</span>}
        </div>
      </div>

      {isBranch && (
        <div className="p-4 bg-canvas-border/40 border border-canvas-border rounded-xl text-sm text-espresso-ink" data-testid="branch-readonly-banner">
          <b>View-only.</b> As a branch user you can scan and view your uploads, but only accounting staff can edit or publish this document.
        </div>
      )}

      {doc.status === "UNASSIGNED" && (
        <UnassignedBanner doc={doc} businesses={businesses} onAssigned={() => setTick((v) => v + 1)} />
      )}

      {doc.status === "DUPLICATE_SUSPECT" && (
        <div className="p-5 bg-status-duplicate/10 border border-status-duplicate/20 rounded-xl flex items-start gap-4" data-testid="duplicate-banner">
          <AlertTriangle className="w-5 h-5 text-status-duplicate mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-status-duplicate">Potential duplicate detected</div>
            <div className="text-sm text-espresso-ink mt-1">
              This document matches an existing record{doc.duplicate_of && <> — <Link to={`/documents/${doc.duplicate_of}`} className="underline">view original</Link></>}. Review carefully before proceeding.
            </div>
            <div className="mt-3 flex items-center gap-3 text-sm">
              <button onClick={markNotDup} className="font-medium text-status-duplicate hover:underline" data-testid="mark-not-dup">
                Mark as not a duplicate
              </button>
              {canPush && (
                <>
                  <span className="text-espresso-ink/30">·</span>
                  <button onClick={syncDraft} disabled={syncing} className="font-medium text-espresso hover:underline disabled:opacity-50" data-testid="publish-anyway-btn">
                    {syncing ? "Publishing…" : "Publish anyway"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {doc.status === "PUBLISHED" && canPush && (
        <div className="p-4 bg-canvas-border/30 border border-canvas-border rounded-xl flex items-center justify-between gap-4" data-testid="published-banner">
          <div className="text-sm text-espresso-ink">
            <span className="font-semibold text-espresso">Published</span>
            {doc.publish?.external_id && (
              <> · <span className="font-mono text-xs bg-canvas-border/40 px-1.5 py-0.5 rounded">{doc.publish.provider}:{doc.publish.external_id.slice(0, 8)}…</span></>
            )}
            {doc.publish?.at && <> · <span className="text-espresso-ink/60">{new Date(doc.publish.at).toLocaleString()}</span></>}
          </div>
          <button
            onClick={async () => {
              const ok = window.confirm(
                "Reset this document back to DRAFT?\n\n" +
                "This UNLINKS DocHub from the accounting system's invoice (Xero / Zoho record stays as-is) " +
                "and lets you edit and re-publish. Use this to recover from iter84's wrong-link regression " +
                "where DocHub linked to unrelated bills."
              );
              if (!ok) return;
              try {
                const res = await documentsApi.resetPublish(id);
                toast.success(`Reset — was linked to ${res.previous_external_id?.slice(0, 8)}…`);
                setTick((v) => v + 1);
              } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
            }}
            data-testid="reset-publish-btn"
            className="text-sm font-medium text-espresso hover:underline whitespace-nowrap"
          >
            Reset publish
          </button>
        </div>
      )}

      <div className={layout === "vertical" ? "space-y-6" : "grid grid-cols-1 lg:grid-cols-5 gap-6 lg:items-start"} data-testid="doc-layout-container">
        {/* Preview: full-width in vertical mode, 2/5 sidebar in horizontal.
            Sticky lives on the grid item itself (not a wrapper) — otherwise
            `self-start` shrinks the parent to its content height and the
            sticky child has no track to travel on, so it never actually
            "freezes" as the right column scrolls.
            In vertical mode we ALSO make it sticky at the top of the scroll
            container (capped at ~55vh) so the receipt stays visible while
            the user works down through the line items below — this is what
            the user asked for repeatedly ("freeze the doc preview"). */}
        <div
          className={
            layout === "vertical"
              ? "lg:sticky lg:top-6 lg:z-10 lg:max-h-[55vh] lg:overflow-y-auto bg-canvas"
              : "lg:col-span-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto"
          }
          data-testid="preview-pane"
        >
          <div>
            <div className="bg-canvas-border/40 border border-canvas-border rounded-2xl p-3 shadow-soft relative">
              {!isPdf && (
                <div className="absolute top-4 right-4 z-10 flex gap-1 items-center">
                  <button
                    onClick={() => setPreviewZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                    data-testid="zoom-out-btn"
                    title="Zoom out"
                    className="w-8 h-8 rounded-md bg-white/90 backdrop-blur border border-canvas-border hover:bg-white text-espresso flex items-center justify-center shadow-soft disabled:opacity-40"
                    disabled={previewZoom <= 0.5}
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewZoom(1)}
                    data-testid="zoom-reset-btn"
                    title={`Reset zoom (currently ${Math.round(previewZoom * 100)}%)`}
                    className="px-2 h-8 rounded-md bg-white/90 backdrop-blur border border-canvas-border hover:bg-white text-espresso flex items-center justify-center shadow-soft text-xs font-semibold tabular-nums"
                  >
                    {Math.round(previewZoom * 100)}%
                  </button>
                  <button
                    onClick={() => setPreviewZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
                    data-testid="zoom-in-btn"
                    title="Zoom in"
                    className="w-8 h-8 rounded-md bg-white/90 backdrop-blur border border-canvas-border hover:bg-white text-espresso flex items-center justify-center shadow-soft disabled:opacity-40"
                    disabled={previewZoom >= 3}
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-canvas-border mx-1" />
                  <button
                    onClick={() => setPreviewRotation((r) => (r + 270) % 360)}
                    data-testid="rotate-left-btn"
                    title="Rotate 90° left"
                    className="w-8 h-8 rounded-md bg-white/90 backdrop-blur border border-canvas-border hover:bg-white text-espresso flex items-center justify-center shadow-soft"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewRotation((r) => (r + 90) % 360)}
                    data-testid="rotate-right-btn"
                    title="Rotate 90° right"
                    className="w-8 h-8 rounded-md bg-white/90 backdrop-blur border border-canvas-border hover:bg-white text-espresso flex items-center justify-center shadow-soft"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                </div>
              )}
              {isPdf ? (
                <embed src={imgUrl} type="application/pdf" className={`w-full rounded-lg ${layout === "vertical" ? "h-[85vh]" : "h-[70vh]"}`} />
              ) : (
                <div className={`overflow-auto rounded-lg ${previewZoom > 1 ? "max-h-[85vh]" : "flex items-center justify-center"}`} style={{ minHeight: previewRotation % 180 !== 0 ? "70vh" : undefined }}>
                  <img
                    src={imgUrl}
                    alt="scan"
                    data-testid="doc-image"
                    className="max-w-full rounded-lg transition-transform duration-300"
                    style={{ transform: `rotate(${previewRotation}deg) scale(${previewZoom})`, transformOrigin: previewZoom > 1 ? "top left" : "center", maxHeight: previewZoom <= 1 ? "85vh" : "none" }}
                  />
                </div>
              )}
            </div>
            <div className="text-xs text-espresso-ink/50 mt-3">
              {biz?.name} · {doc.source} · {doc.created_at && new Date(doc.created_at).toLocaleString()}
            </div>
            {(isMaster(user) || businesses.length > 1) && !doc.locked && doc.status !== "UNASSIGNED" && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Reassign to business</label>
                <select
                  data-testid="reassign-select"
                  onChange={(e) => reassign(e.target.value)}
                  value=""
                  className="w-full mt-1.5 px-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus"
                >
                  <option value="">Choose…</option>
                  {businesses.filter((b) => b.id !== doc.business_id).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Extracted fields: full-width in vertical mode, 3/5 in horizontal */}
        <div className={layout === "vertical" ? "space-y-6" : "lg:col-span-3 space-y-6"} data-testid="form-pane">
          {(doc.status === "PROCESSING" || doc.status === "QUEUED") && (
            <div className="bg-white border border-canvas-border rounded-2xl p-6 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-gold" />
              <div className="flex-1">
                <div className="font-semibold text-espresso">AI extraction in progress</div>
                <div className="text-sm text-espresso-ink/60">Claude Sonnet 4.5 is reading your scan… Stuck for more than a couple of minutes? Retry it.</div>
              </div>
              <RetryButton id={id} onDone={() => setTick((v) => v + 1)} />
            </div>
          )}
          {doc.status === "ERROR" && (
            <div className="p-4 bg-status-duplicate/10 border border-status-duplicate/20 rounded-lg text-status-duplicate text-sm flex items-center gap-3">
              <span className="flex-1">OCR failed: {doc.error}</span>
              <RetryButton id={id} onDone={() => setTick((v) => v + 1)} />
            </div>
          )}

          <div className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-espresso">Extracted fields</h2>
              <button onClick={() => setTick((v) => v + 1)} className="text-xs text-espresso-ink/60 hover:text-espresso inline-flex items-center gap-1" data-testid="refresh-doc">
                <RefreshCcw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {ext && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <VendorAutocomplete
                    label="Vendor / Supplier"
                    testid="ext-vendor"
                    value={ext.vendor_name}
                    onChange={(name, picked) => {
                      // Free-typing → just update the name. Picking an existing
                      // vendor rule → also cascade its defaults onto the header
                      // (tax code, expense account, payment method) AND branch
                      // (doc-level field, needs its own PATCH). iter82 request:
                      // "when I select a vendor, the vendor default rules
                      // should populate".
                      if (!picked) {
                        update("vendor_name", name);
                        return;
                      }
                      setExt((e) => {
                        const nextExt = { ...e, vendor_name: name };
                        if (picked.default_tax_code_id) nextExt.tax_code_id = picked.default_tax_code_id;
                        if (picked.default_expense_account) nextExt.expense_account = picked.default_expense_account;
                        if (picked.default_payment_method) nextExt.payment_method = picked.default_payment_method;
                        // Cascade tax_code_id → line items with new rate applied
                        if (picked.default_tax_code_id) {
                          const rate = vats.find((v) => v.id === picked.default_tax_code_id)?.rate || 0;
                          const r2 = (n) => +Number(n || 0).toFixed(2);
                          nextExt.line_items = (e.line_items || []).map((li) => {
                            const netAmt = parseFloat(li.net ?? li.line_total) || 0;
                            const newVat = r2(netAmt * (rate || 0) / 100);
                            return {
                              ...li,
                              tax_code_id: picked.default_tax_code_id,
                              expense_account: picked.default_expense_account || li.expense_account,
                              vat: newVat,
                              total: r2(netAmt + newVat),
                            };
                          });
                          const netHdr = parseFloat(nextExt.net_amount ?? nextExt.subtotal) || 0;
                          const newVatHdr = r2(netHdr * rate / 100);
                          nextExt.vat_amount = newVatHdr;
                          nextExt.gross_amount = r2(netHdr + newVatHdr);
                          nextExt.total = nextExt.gross_amount;
                        } else if (picked.default_expense_account) {
                          // Only account changed — cascade without touching VAT
                          nextExt.line_items = (e.line_items || []).map((li) => ({
                            ...li,
                            expense_account: picked.default_expense_account,
                          }));
                        }
                        return nextExt;
                      });
                      // Branch is a doc-level field — persist via PATCH so the
                      // banner + reassign flow stay in sync
                      if (picked.default_branch_id && picked.default_branch_id !== doc.branch_id) {
                        (async () => {
                          try {
                            await documentsApi.update(id, { branch_id: picked.default_branch_id });
                            setTick((v) => v + 1);
                          } catch { /* silent — user can pick manually */ }
                        })();
                      }
                    }}
                    disabled={!canEdit}
                    vendors={vendors}
                  />
                  <Field label="Invoice / Receipt #" testid="ext-invoice" value={ext.invoice_number} onChange={(v) => update("invoice_number", v)} disabled={!canEdit} />
                  <Field label="Invoice Date" testid="ext-date" type="date" value={ext.invoice_date} onChange={(v) => update("invoice_date", v)} disabled={!canEdit} />
                  <Field label="Due Date" testid="ext-due" type="date" value={ext.due_date} onChange={(v) => update("due_date", v)} disabled={!canEdit} />
                  <Field label="Currency" testid="ext-currency" value={ext.currency} onChange={(v) => update("currency", v)} disabled={!canEdit} />
                  <PaymentMethodSelect
                    testid="ext-payment"
                    value={ext.payment_method}
                    onChange={(v) => update("payment_method", v)}
                    disabled={!canEdit}
                    accounts={accounts}
                    hidePending={biz?.accounting === "zoho" && (doc.document_type || biz?.default_document_type) === "expense" && (doc.kind || "bill") !== "credit_note"}
                    required={biz?.accounting === "zoho" && (doc.document_type || biz?.default_document_type) === "expense" && (doc.kind || "bill") !== "credit_note"}
                  />
                  {/* Cash Up: per-document Shift Payout toggle (iter102).
                      Only rendered when the business has Cash Up enabled —
                      keeps the header clean for entities that don't use it.
                      Sends `is_shift_payout: true|false` on the Cash Up POST
                      that fires after publish. Was previously a vendor-level
                      flag, but the same vendor can produce regular expenses
                      AND shift payouts on different days so the decision
                      belongs to the reviewer, per doc. */}
                  {biz?.cashup_enabled && (
                    <div className="col-span-2">
                      <label className="flex items-center gap-2 text-sm text-espresso-ink cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="ext-cashup-shift"
                          disabled={!canEdit}
                          checked={!!ext.cashup_is_shift_payout}
                          onChange={(e) => update("cashup_is_shift_payout", e.target.checked)}
                          className="w-4 h-4 accent-espresso cursor-pointer"
                        />
                        <span>
                          Cash Up: this is a <b>shift payout</b>
                          <span className="text-xs text-espresso-ink/60 ml-1">(sends <code className="text-[10px]">is_shift_payout: true</code> when published)</span>
                        </span>
                      </label>
                    </div>
                  )}
                  <div>
                    <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Tax code</label>
                    <select
                      data-testid="ext-tax-code"
                      disabled={!canEdit}
                      value={ext.tax_code_id || ""}
                      onChange={(e) => update("tax_code_id", e.target.value)}
                      className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border rounded-lg text-sm brand-focus disabled:bg-canvas-border/30"
                    >
                      <option value="">— none —</option>
                      {vats.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.rate}%)</option>)}
                    </select>
                  </div>
                  <AccountAutocomplete
                    label="Default expense account"
                    testid="ext-expense-account"
                    value={ext.expense_account || ""}
                    onChange={(v) => update("expense_account", v)}
                    disabled={!canEdit}
                    accounts={expenseAccounts(accounts)}
                    emptyLabel="— use vendor default —"
                    hint="Line items without an account use this. Line items with their own account override it."
                  />
                  <div>
                    <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Branch</label>
                    <select
                      data-testid="ext-branch"
                      disabled={!canEdit}
                      value={doc.branch_id || ""}
                      onChange={async (e) => {
                        const newBranch = e.target.value || null;
                        // Cascade: propagate branch to line items that don't have their own pick
                        const nextExt = { ...ext, line_items: (ext?.line_items || []).map((li) => li.branch_id ? li : { ...li, branch_id: newBranch || "" }) };
                        setExt(nextExt);
                        // Persist the current in-memory ext ALONGSIDE the branch change so
                        // the tick refresh below doesn't wipe unsaved edits (same bug class
                        // as iter62 — anything that calls setTick must first save ext).
                        await documentsApi.update(id, { branch_id: newBranch, extracted: nextExt });
                        setTick((v) => v + 1);
                      }}
                      className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border text-sm brand-focus disabled:bg-canvas-border/30"
                    >
                      <option value="">— none —</option>
                      {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  {/* Document type selector — provider-aware.
                      Xero  : Bill | Credit Note
                      Zoho  : Bill | Receipt | Credit Note (Receipt = Zoho Expense API) */}
                  {(biz?.accounting === "xero" || biz?.accounting === "zoho") && (() => {
                    // Derive current choice from the two underlying fields
                    const isCredit = (doc.kind || "bill") === "credit_note";
                    const zohoIsExpense = (doc.document_type || biz?.default_document_type || "bill") === "expense";
                    let current;
                    if (isCredit) current = "credit_note";
                    else if (biz?.accounting === "zoho" && zohoIsExpense) current = "receipt";
                    else current = "bill";

                    const applyChoice = async (choice) => {
                      const updates = {};
                      if (choice === "credit_note") {
                        updates.kind = "credit_note";
                        // document_type is irrelevant for credit notes
                      } else if (choice === "receipt") {
                        updates.kind = "bill";
                        updates.document_type = "expense";
                      } else {
                        updates.kind = "bill";
                        if (biz?.accounting === "zoho") updates.document_type = "bill";
                      }
                      // Save the current ext alongside the doctype change so the
                      // tick refresh doesn't wipe unsaved edits (same bug as branch/vendor-rule).
                      updates.extracted = ext;
                      await documentsApi.update(id, updates);
                      setTick((v) => v + 1);
                    };

                    return (
                      <div>
                        <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">
                          {biz.accounting === "xero" ? "Xero" : "Zoho"} document type
                        </label>
                        <select
                          data-testid="ext-doctype"
                          disabled={!canEdit}
                          value={current}
                          onChange={(e) => applyChoice(e.target.value)}
                          className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border text-sm brand-focus disabled:bg-canvas-border/30"
                        >
                          <option value="bill">Bill (needs vendor)</option>
                          {biz.accounting === "zoho" && (
                            <option value="receipt">Receipt (already paid · vendor optional)</option>
                          )}
                          <option value="credit_note">Credit Note (refund / return)</option>
                        </select>
                      </div>
                    );
                  })()}
                </div>

                {/* NET / VAT / GROSS totals block + reconciliation */}
                <div className="grid grid-cols-3 gap-3 mt-2" data-testid="totals-block">
                  <TotalBox label="Net (excl VAT)" testid="ext-net" value={ext.net_amount ?? ext.subtotal ?? 0} onChange={(n) => { setExt((e) => ({ ...e, net_amount: n, subtotal: n, gross_amount: +(n + (e.vat_amount || 0)).toFixed(2), total: +(n + (e.vat_amount || 0)).toFixed(2) })); }} disabled={!canEdit} />
                  <TotalBox label="VAT" testid="ext-vat" value={ext.vat_amount ?? 0} onChange={(n) => { setExt((e) => ({ ...e, vat_amount: n, gross_amount: +((e.net_amount || e.subtotal || 0) + n).toFixed(2), total: +((e.net_amount || e.subtotal || 0) + n).toFixed(2) })); }} disabled={!canEdit} />
                  <TotalBox label="Gross (incl VAT)" testid="ext-gross" value={ext.gross_amount ?? ext.total ?? 0} onChange={(n) => { setExt((e) => ({ ...e, gross_amount: n, total: n })); }} disabled={!canEdit} accent />
                </div>
                {(() => {
                  const net = +(ext.net_amount ?? ext.subtotal ?? 0);
                  const vat = +(ext.vat_amount ?? 0);
                  const gross = +(ext.gross_amount ?? ext.total ?? 0);
                  // Line items reconcile to GROSS (incl VAT), not net — each
                  // line's `total` field already carries net + VAT, so summing
                  // them should equal the document's gross total. This matches
                  // how Xero/Zoho persist line items on a bill.
                  const linesSum = +((ext.line_items || []).reduce((s, l) => {
                    const t = parseFloat(l.total);
                    if (!isNaN(t) && t) return s + t;
                    const n = parseFloat(l.net ?? l.line_total) || 0;
                    const vv = parseFloat(l.vat) || 0;
                    return s + n + vv;
                  }, 0)).toFixed(2);
                  const headerOff = +Math.abs((net + vat) - gross).toFixed(2);
                  const linesOff = +Math.abs(linesSum - gross).toFixed(2);
                  const headerBad = gross > 0 && headerOff > 0.05;
                  const linesBad = gross > 0 && (ext.line_items || []).length > 0 && linesOff > 0.05;
                  if (!headerBad && !linesBad) return (
                    <div className="text-xs text-status-synced font-semibold flex items-center gap-1.5" data-testid="recon-ok">
                      <span>✓ Reconciled — net + VAT = gross{ext.line_items?.length ? ` and line items sum to ${gross.toFixed(2)} (gross)` : ""}</span>
                    </div>
                  );
                  return (
                    <div className="p-3 rounded-lg bg-status-duplicate/10 border border-status-duplicate/30 text-sm text-status-duplicate" data-testid="recon-warning">
                      <div className="font-semibold">Totals don&apos;t reconcile</div>
                      {headerBad && <div className="mt-0.5">Net ({net.toFixed(2)}) + VAT ({vat.toFixed(2)}) = {(net + vat).toFixed(2)}, but gross is {gross.toFixed(2)} — off by {headerOff}.</div>}
                      {linesBad && <div className="mt-0.5">Line items sum to {linesSum.toFixed(2)} (gross), but header gross is {gross.toFixed(2)} — off by {linesOff}. Pick a tax code on the header to auto-fill VAT on lines.</div>}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-lg font-semibold text-espresso">Line items</h3>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { const next = layout === "horizontal" ? "vertical" : "horizontal"; setLayout(next); localStorage.setItem("dochub_doc_layout", next); }} data-testid="layout-toggle" className="text-xs px-2 py-1 border border-canvas-border rounded-md text-espresso-ink hover:bg-canvas-border/50">
                        {layout === "horizontal" ? "Vertical split" : "Horizontal split"}
                      </button>
                      {canEdit && (
                        <button
                          onClick={extractLines}
                          disabled={extracting}
                          data-testid="extract-lines-btn"
                          title="Re-run OCR to pull line items from the receipt"
                          className="text-xs px-2 py-1 border border-canvas-border rounded-md text-espresso-ink hover:bg-canvas-border/50 inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                          Extract line items
                        </button>
                      )}
                      {canEdit && biz?.connected && (doc.kind || "bill") !== "credit_note" && (
                        <button onClick={openPoModal} data-testid="match-po-btn" className="text-xs px-2 py-1 border border-canvas-border rounded-md text-espresso-ink hover:bg-canvas-border/50 inline-flex items-center gap-1">
                          <ClipboardList className="w-3 h-3" /> Match PO
                        </button>
                      )}
                      {canEdit && <button onClick={addLine} className="text-sm text-gold hover:text-gold-light font-medium" data-testid="add-line">+ Add line</button>}
                    </div>
                  </div>
                  <div className="border border-canvas-border rounded-lg overflow-x-auto">
                    <table className="w-full text-xs" data-testid="line-items-table">
                      <thead className="bg-canvas">
                        <tr className="uppercase tracking-wider text-espresso-ink/60">
                          <th className="text-left px-2 py-2 min-w-[180px]">Description</th>
                          <th className="text-left px-2 py-2 w-32">Branch</th>
                          <th className="text-left px-2 py-2 w-28">Tax code</th>
                          <th className="text-left px-2 py-2 w-28">Expense a/c</th>
                          <th className="text-right px-2 py-2 w-16">Qty</th>
                          <th className="text-right px-2 py-2 w-20">Unit</th>
                          <th className="text-right px-2 py-2 w-24">Net</th>
                          <th className="text-right px-2 py-2 w-20">VAT</th>
                          <th className="text-right px-2 py-2 w-24">Total</th>
                          {canEdit && <th className="w-6"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(ext.line_items || []).map((li, i) => (
                          <tr key={i} className="border-t border-canvas-border">
                            <td className="px-2 py-1.5"><input disabled={!canEdit} data-testid={`li-desc-${i}`} value={li.description || ""} onChange={(e) => updateLine(i, "description", e.target.value)} className="w-full bg-transparent brand-focus rounded" /></td>
                            <td className="px-2 py-1.5">
                              <select disabled={!canEdit} data-testid={`li-branch-${i}`} value={li.branch_id || ""} onChange={(e) => updateLine(i, "branch_id", e.target.value)} className="w-full bg-transparent brand-focus rounded text-xs">
                                <option value="">—</option>
                                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <select disabled={!canEdit} data-testid={`li-tax-${i}`} value={li.tax_code_id || ""} onChange={(e) => updateLine(i, "tax_code_id", e.target.value)} className="w-full bg-transparent brand-focus rounded text-xs">
                                <option value="">—</option>
                                {vats.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <AccountAutocomplete
                                testid={`li-account-${i}`}
                                value={li.expense_account || ""}
                                onChange={(v) => updateLine(i, "expense_account", v)}
                                disabled={!canEdit}
                                accounts={expenseAccounts(accounts)}
                                emptyLabel="— header —"
                                compact
                              />
                            </td>
                            <td className="px-2 py-1.5"><NumInput disabled={!canEdit} testid={`li-qty-${i}`} value={li.quantity ?? 0} onChange={(v) => updateLine(i, "quantity", v)} className="w-full text-right bg-transparent brand-focus rounded" /></td>
                            <td className="px-2 py-1.5"><NumInput disabled={!canEdit} testid={`li-unit-${i}`} value={li.unit_price ?? 0} onChange={(v) => updateLine(i, "unit_price", v)} className="w-full text-right bg-transparent brand-focus rounded" /></td>
                            <td className="px-2 py-1.5"><NumInput disabled={!canEdit} testid={`li-net-${i}`} value={li.net ?? li.line_total ?? 0} onChange={(v) => updateLine(i, "net", v)} className="w-full text-right bg-transparent brand-focus rounded font-semibold" /></td>
                            <td className="px-2 py-1.5"><NumInput disabled={!canEdit} testid={`li-vat-${i}`} value={li.vat ?? 0} onChange={(v) => updateLine(i, "vat", v)} className="w-full text-right bg-transparent brand-focus rounded" /></td>
                            <td className="px-2 py-1.5"><NumInput disabled={!canEdit} testid={`li-total-${i}`} value={li.total ?? ((li.net || li.line_total || 0) + (li.vat || 0))} onChange={(v) => updateLine(i, "total", v)} className="w-full text-right bg-transparent brand-focus rounded font-semibold text-espresso" /></td>
                            {canEdit && <td className="px-1 py-1.5"><button onClick={() => removeLine(i)} data-testid={`li-del-${i}`} className="text-status-duplicate hover:text-status-duplicate/70 text-xs">×</button></td>}
                          </tr>
                        ))}
                        {(!ext.line_items || ext.line_items.length === 0) && (
                          <tr><td colSpan={10} className="px-3 py-8 text-center text-espresso-ink/50 text-sm">No line items extracted</td></tr>
                        )}
                      </tbody>
                      {ext.line_items?.length > 0 && (() => {
                        // Dext-style footer: sum each numeric column across the line items,
                        // compare against the header-extracted totals, and expose the variance
                        // ("Out by") so the user can drag columns until Line items = Item total = 0.00 out.
                        const sumNet = +(ext.line_items).reduce((s, l) => s + (parseFloat(l.net ?? l.line_total) || 0), 0).toFixed(2);
                        const sumVat = +(ext.line_items).reduce((s, l) => s + (parseFloat(l.vat) || 0), 0).toFixed(2);
                        const sumTotal = +(ext.line_items).reduce((s, l) => s + (parseFloat(l.total ?? (parseFloat(l.net ?? l.line_total) || 0) + (parseFloat(l.vat) || 0)) || 0), 0).toFixed(2);
                        const hNet = +(parseFloat(ext.net_amount ?? ext.subtotal ?? 0) || 0).toFixed(2);
                        const hVat = +(parseFloat(ext.vat_amount) || 0).toFixed(2);
                        const hGross = +(parseFloat(ext.gross_amount ?? ext.total ?? 0) || 0).toFixed(2);
                        const outNet = +(sumNet - hNet).toFixed(2);
                        const outVat = +(sumVat - hVat).toFixed(2);
                        const outGross = +(sumTotal - hGross).toFixed(2);
                        const cls = (n) => Math.abs(n) < 0.05 ? "text-status-synced" : "text-status-duplicate";
                        const fmt = (n) => (n > 0 ? "+" : "") + n.toFixed(2);
                        return (
                          <tfoot data-testid="line-items-footer" className="text-xs">
                            <tr className="border-t-2 border-canvas-border bg-canvas/50">
                              <td colSpan={6} className="px-2 py-2 text-right font-semibold text-espresso-ink/70 uppercase tracking-wider">Line items</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-lines-net">{sumNet.toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-lines-vat">{sumVat.toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-lines-total">{sumTotal.toFixed(2)}</td>
                              {canEdit && <td></td>}
                            </tr>
                            <tr className="bg-canvas/30">
                              <td colSpan={6} className="px-2 py-2 text-right font-semibold text-espresso-ink/70 uppercase tracking-wider">Out by</td>
                              <td className={`px-2 py-2 text-right font-semibold tabular-nums ${cls(outNet)}`} data-testid="footer-out-net">{fmt(outNet)}</td>
                              <td className={`px-2 py-2 text-right font-semibold tabular-nums ${cls(outVat)}`} data-testid="footer-out-vat">{fmt(outVat)}</td>
                              <td className={`px-2 py-2 text-right font-semibold tabular-nums ${cls(outGross)}`} data-testid="footer-out-total">{fmt(outGross)}</td>
                              {canEdit && <td></td>}
                            </tr>
                            <tr className="bg-espresso/5 border-t border-canvas-border">
                              <td colSpan={6} className="px-2 py-2 text-right font-semibold text-espresso uppercase tracking-wider">Item total (extracted)</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-item-net">{hNet.toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-item-vat">{hVat.toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-bold text-espresso tabular-nums" data-testid="footer-item-total">{hGross.toFixed(2)}</td>
                              {canEdit && <td></td>}
                            </tr>
                          </tfoot>
                        );
                      })()}
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          {canEdit && (
            <div className="sticky bottom-0 -mx-8 px-8 py-4 bg-canvas/95 backdrop-blur-md border-t border-canvas-border flex items-center justify-end gap-3">
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 border border-canvas-border rounded-lg text-sm font-semibold text-espresso hover:bg-white transition-colors" data-testid="save-doc">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-4 h-4" /> Save
              </button>
              {doc.status === "DRAFT_IN_DOCHUB" && canPush && (
                <button onClick={syncDraft} disabled={syncing} className="inline-flex items-center gap-2 px-5 py-2.5 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light transition-colors" data-testid="publish-btn">
                  {syncing && <Loader2 className="w-4 h-4 animate-spin" />} <Send className="w-4 h-4" /> Publish to {biz?.accounting === "xero" ? "Xero" : "Zoho Books"}
                </button>
              )}
              {doc.status === "DRAFT_IN_DOCHUB" && effectiveAccount && (
                <div className="text-xs text-espresso-ink/60 italic" data-testid="effective-account-hint">
                  Will post to <span className="font-mono text-espresso font-semibold">{effectiveAccount}</span>
                  {matchedVendor ? " · from vendor rule" : ""}
                </div>
              )}
              {matchedVendor && vendorRuleDiff.length > 0 && canEdit && (
                <div className="w-full mt-2 p-3 rounded-lg bg-gold-soft/40 border border-gold/40 text-xs text-espresso" data-testid="vendor-rule-diff">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold mb-1">Vendor rule differs from this document</div>
                      <ul className="space-y-0.5">
                        {vendorRuleDiff.map((d) => (
                          <li key={d.field}><b>{d.field}:</b> was <span className="line-through text-espresso-ink/60">{d.from}</span> → <span className="font-semibold text-espresso">{d.to}</span></li>
                        ))}
                      </ul>
                    </div>
                    <button onClick={updateVendorRule} data-testid="update-vendor-rule-btn" className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-espresso text-canvas rounded-md hover:bg-espresso-light">
                      Update vendor rule
                    </button>
                  </div>
                </div>
              )}
              {doc.status === "DRAFT_IN_DOCHUB" && !canPush && (
                <div className="text-xs text-espresso-ink/60 italic">Only an Accounting User can publish this document.</div>
              )}
            </div>
          )}

          <div className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft" data-testid="doc-timeline">
            <h3 className="font-display text-lg font-bold text-espresso mb-4">Timeline</h3>
            {timeline.length === 0 ? (
              <div className="text-sm text-espresso-ink/50">No events logged yet.</div>
            ) : (
              <ol className="space-y-3">
                {timeline.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 text-sm" data-testid={`timeline-row-${e.id}`}>
                    <div className="mt-1.5 w-2 h-2 rounded-full bg-gold shrink-0" />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-semibold text-espresso">{e.actor_email || "system"}</span>
                        <span className="text-espresso-ink/80">{e.action.replace(/^document\./, "").replace(/_/g, " ")}</span>
                        <span className="text-xs text-espresso-ink/50">{new Date(e.at).toLocaleString()}</span>
                      </div>
                      {e.meta && Object.keys(e.meta).length > 0 && (
                        <div className="text-xs text-espresso-ink/60 mt-0.5">
                          {Object.entries(e.meta).slice(0, 4).map(([k, v]) => (
                            <span key={k} className="mr-3"><span className="uppercase tracking-widest text-[10px] text-espresso-ink/40">{k}</span> {typeof v === "object" ? JSON.stringify(v).slice(0, 40) : String(v).slice(0, 60)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

        </div>
      </div>
      {poOpen && (
        <POMatchModal
          onClose={() => setPoOpen(false)}
          onSelect={applyPO}
          pos={pos}
          loading={poLoading}
          hasVendor={!!matchedVendor?.external_id}
          filterVendor={poFilterVendor}
          setFilterVendor={setPoFilterVendor}
          reload={openPoModal}
          provider={biz?.accounting}
        />
      )}
    </div>
  );
}

function POMatchModal({ onClose, onSelect, pos, loading, hasVendor, filterVendor, setFilterVendor, reload, provider }) {
  const [q, setQ] = useState("");
  const filtered = (pos || []).filter((p) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (p.number || "").toLowerCase().includes(s)
      || (p.reference || "").toLowerCase().includes(s)
      || (p.vendor_name || "").toLowerCase().includes(s);
  });
  return createPortal(
    <div className="fixed inset-0 z-[1100] bg-espresso/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="po-match-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-canvas-border flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-espresso">Match to open Purchase Order</h3>
            <div className="text-xs text-espresso-ink/60 mt-0.5">Selecting a PO replaces the current line items with the PO&apos;s lines.</div>
          </div>
          <button onClick={onClose} data-testid="po-modal-close" className="w-8 h-8 rounded-md hover:bg-canvas-border/40 flex items-center justify-center text-espresso-ink"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-canvas-border flex items-center gap-3 flex-wrap">
          <input
            data-testid="po-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by PO number, reference, vendor…"
            className="flex-1 min-w-[200px] px-3 py-2 bg-canvas border border-canvas-border rounded-lg text-sm brand-focus"
          />
          {hasVendor && (
            <label className="text-xs text-espresso-ink inline-flex items-center gap-1.5 cursor-pointer" data-testid="po-vendor-filter">
              <input type="checkbox" checked={filterVendor} onChange={(e) => { setFilterVendor(e.target.checked); setTimeout(reload, 0); }} />
              Vendor only
            </label>
          )}
          <span className="text-xs text-espresso-ink/60">{loading ? "" : `${filtered.length} PO${filtered.length === 1 ? "" : "s"}`}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-espresso-ink/60"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading POs from {provider === "xero" ? "Xero" : "Zoho Books"}…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-espresso-ink/60 text-sm">
              No open purchase orders found{hasVendor && filterVendor ? " for this vendor" : ""}.
              {hasVendor && filterVendor && <div className="mt-2"><button className="underline" onClick={() => { setFilterVendor(false); setTimeout(reload, 0); }}>Show all vendors&apos; POs</button></div>}
            </div>
          ) : (
            <ul>
              {filtered.map((p) => (
                <li key={p.id} className="border-b border-canvas-border last:border-0">
                  <button
                    data-testid={`po-row-${p.id}`}
                    onClick={() => onSelect(p)}
                    className="w-full text-left p-4 hover:bg-canvas transition-colors"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-semibold text-espresso">
                          {p.number || "(no number)"}
                          {p.reference && <span className="ml-2 text-xs font-normal text-espresso-ink/60">Ref: {p.reference}</span>}
                        </div>
                        <div className="text-xs text-espresso-ink mt-0.5">{p.vendor_name || "—"} · {p.date || "no date"} · {(p.line_items || []).length} line{(p.line_items || []).length === 1 ? "" : "s"}</div>
                      </div>
                      <div className="text-sm font-bold text-espresso tabular-nums">{p.total != null ? Number(p.total).toFixed(2) : "—"}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function VendorAutocomplete({ label, value, onChange, disabled, testid, vendors }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const q = (value || "").trim().toLowerCase();
  // Case-insensitive substring match; when empty, show top 8 alphabetically so
  // users can browse the existing list before typing.
  const suggestions = (vendors || [])
    .filter((v) => v.name && (q === "" || v.name.toLowerCase().includes(q)))
    .filter((v) => v.name.toLowerCase() !== q) // hide exact match — no point suggesting what's already picked
    .slice(0, 8);
  const exact = q && vendors?.some((v) => v.name?.toLowerCase() === q);

  // Called for BOTH free-typing (v is a raw string, picked is null) and picking
  // a suggestion (v is the vendor name, picked is the full vendor rule). The
  // caller can distinguish — free-typing means don't cascade defaults; picking
  // a rule means apply its defaults to the doc.
  const pick = (name, picked = null) => {
    onChange(name, picked);
    setOpen(false);
    setFocused(-1);
  };

  return (
    <div className="relative">
      <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value ?? ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setFocused(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setFocused((i) => (i + 1) % suggestions.length); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((i) => (i - 1 + suggestions.length) % suggestions.length); }
          else if (e.key === "Enter" && focused >= 0) { e.preventDefault(); pick(suggestions[focused].name); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
        disabled={disabled}
        autoComplete="off"
        placeholder="Start typing — existing vendors will appear"
        className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border rounded-lg text-sm brand-focus disabled:bg-canvas-border/30 disabled:text-espresso-ink/70"
      />
      {!disabled && open && (suggestions.length > 0 || (q && !exact)) && (
        <div
          data-testid="vendor-suggestions"
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-canvas-border rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {suggestions.map((v, i) => (
            <button
              key={v.id}
              type="button"
              data-testid={`vendor-suggestion-${v.id}`}
              onMouseDown={(e) => { e.preventDefault(); pick(v.name, v); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                i === focused ? "bg-canvas-border/60" : "hover:bg-canvas"
              }`}
            >
              <span className="text-espresso">{v.name}</span>
              {v.default_expense_account && (
                <span className="text-[10px] uppercase tracking-widest text-espresso-ink/50 shrink-0">acc {v.default_expense_account}</span>
              )}
            </button>
          ))}
          {q && !exact && (
            <div
              data-testid="vendor-new-hint"
              className="px-3 py-2 text-xs text-espresso-ink/60 border-t border-canvas-border bg-canvas/60"
            >
              No match. Press <b>Save</b> to create <span className="font-semibold text-espresso">&quot;{value}&quot;</span> as a new vendor.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Chart-of-Accounts helpers ----------
// Balance-sheet-ish account types (bank / cash / current liab / credit card /
// asset / equity). Anything else is treated as P&L (expense / income / cost)
// and is offered on the expense-account picker instead of payment-method.
const BALANCE_SHEET_HINTS = ["bank", "cash", "current", "credit", "liab", "equity", "fixed", "prepay", "inventory", "asset"];
const EXPENSE_HINTS = ["expense", "overhead", "cost", "direct", "operating", "salary", "wages"];
function isBalanceSheet(a) {
  const t = String(a?.type || "").toLowerCase();
  return BALANCE_SHEET_HINTS.some((h) => t.includes(h));
}
function isExpense(a) {
  const t = String(a?.type || "").toLowerCase();
  return EXPENSE_HINTS.some((h) => t.includes(h));
}
function expenseAccounts(accounts) {
  // Prefer P&L expense accounts, but for Zoho we want to show EVERY account
  // (user requested full chart access on Zoho — they routinely code things
  // against COGS, direct-cost, and asset accounts that don't match `isExpense`).
  // Filter only clearly-irrelevant income/sales accounts.
  const expense = (accounts || []).filter(isExpense);
  if (expense.length) return (accounts || []).filter((a) => !/income|revenue|sales/i.test(a.type || ""));
  return (accounts || []).filter((a) => !/income|revenue|sales/i.test(a.type || ""));
}
function paymentAccounts(accounts) {
  return (accounts || []).filter(isBalanceSheet);
}

function PaymentMethodSelect({ value, onChange, disabled, testid, accounts, hidePending = false, required = false }) {
  // Searchable payment-method picker. Balance-sheet accounts + "Pending payment"
  // sentinel (hidden when hidePending — e.g. Zoho Receipt/Expense which is
  // "already paid" and must post to a real bank/cash account).
  return (
    <AccountAutocomplete
      label={required ? "Payment method *" : "Payment method"}
      testid={testid}
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      accounts={paymentAccounts(accounts)}
      emptyLabel={required ? "— required —" : "— unspecified —"}
      extraOptions={hidePending ? [] : [{ value: "pending", label: "Pending payment" }]}
      hint={required ? "Required for Receipts — this is the bank / cash / credit-card account that already paid this bill." : undefined}
    />
  );
}

// Shared searchable dropdown for chart-of-accounts pickers. Stores the code (or
// external_id fallback) as the value; displays "code · name" everywhere. Custom
// legacy free-text values are preserved as-is when no account matches.
// The suggestion list is rendered via a React portal into <body> with fixed
// positioning so parent overflow-x-auto / narrow columns don't clip or squash it
// (critical for line-item cells that live inside a horizontally-scrolling table).
function AccountAutocomplete({ label, value, onChange, disabled, testid, accounts, emptyLabel = "— none —", hint, extraOptions = [], compact = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(-1);
  const inputRef = useRef(null);
  const [popoverRect, setPopoverRect] = useState(null);

  // Compute display text for the current stored value
  const matchedAccount = (accounts || []).find((a) => (a.code || a.external_id) === value);
  const matchedExtra = extraOptions.find((o) => o.value === value);
  const displayText = matchedAccount
    ? `${matchedAccount.code ? matchedAccount.code + " · " : ""}${matchedAccount.name}`
    : matchedExtra
      ? matchedExtra.label
      : value; // free-text / custom legacy value

  // When the popover is open, use the live query; otherwise show the selected item
  const inputValue = open ? query : (displayText || "");
  const q = (query || "").trim().toLowerCase();

  // Filter accounts by code or name substring (case-insensitive)
  const accountMatches = (accounts || []).filter((a) => {
    if (!q) return true;
    return (a.code || "").toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q);
  }).slice(0, 25);
  const extraMatches = extraOptions.filter((o) => !q || o.label.toLowerCase().includes(q));
  const allRows = [...extraMatches.map((o) => ({ type: "extra", ...o })), ...accountMatches.map((a) => ({ type: "account", ...a }))];

  const openPopover = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPopoverRect({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 288) });
    }
    setQuery("");
    setOpen(true);
  };

  // Re-position on scroll/resize so the popover follows the input
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setPopoverRect({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 288) });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const pick = (row) => {
    const v = row.type === "extra" ? row.value : (row.code || row.external_id);
    onChange(v);
    setOpen(false);
    setQuery("");
    setFocused(-1);
  };

  const clear = () => { onChange(""); setQuery(""); setFocused(-1); };

  const baseInputClass = compact
    ? "w-full bg-transparent brand-focus rounded text-xs px-1 py-0.5"
    : "w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border rounded-lg text-sm brand-focus disabled:bg-canvas-border/30 disabled:text-espresso-ink/70";

  const popover = (open && !disabled && popoverRect) ? createPortal(
    <div
      data-testid={`${testid}-suggestions`}
      style={{ position: "fixed", left: popoverRect.left, top: popoverRect.top, width: popoverRect.width, zIndex: 1000 }}
      className="bg-white border border-canvas-border rounded-lg shadow-xl overflow-hidden max-h-72 overflow-y-auto"
      onMouseDown={(e) => e.preventDefault()}  /* keep focus on input while clicking */
    >
      {allRows.length === 0 ? (
        <div className="px-3 py-2 text-xs text-espresso-ink/50">
          No matching accounts. Have you synced the chart of accounts?
        </div>
      ) : allRows.map((row, i) => (
        <button
          key={row.type === "extra" ? `x-${row.value}` : row.id}
          type="button"
          data-testid={`${testid}-suggestion-${row.type === "extra" ? row.value : (row.code || row.external_id)}`}
          onMouseDown={(e) => { e.preventDefault(); pick(row); }}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 ${
            i === focused ? "bg-canvas-border/60" : "hover:bg-canvas"
          }`}
        >
          {row.type === "extra" ? (
            <span className="text-espresso font-medium">{row.label}</span>
          ) : (
            <>
              <span className="font-mono text-xs text-espresso-ink/70 shrink-0 w-14">{row.code || ""}</span>
              <span className="flex-1 text-espresso truncate">{row.name}</span>
            </>
          )}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      {label && <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">{label}</label>}
      <input
        ref={inputRef}
        data-testid={testid}
        type="text"
        value={inputValue}
        onChange={(e) => { setQuery(e.target.value); if (!open) openPopover(); setFocused(-1); }}
        onFocus={openPopover}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={(e) => {
          if (!open) { if (e.key === "ArrowDown") { openPopover(); e.preventDefault(); } return; }
          if (e.key === "ArrowDown") { e.preventDefault(); setFocused((i) => Math.min(allRows.length - 1, i + 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((i) => Math.max(0, i - 1)); }
          else if (e.key === "Enter" && focused >= 0) { e.preventDefault(); pick(allRows[focused]); }
          else if (e.key === "Escape") { setOpen(false); setQuery(""); }
          else if (e.key === "Backspace" && !query && value) { e.preventDefault(); clear(); }
        }}
        disabled={disabled}
        autoComplete="off"
        placeholder={open ? "Type to search…" : emptyLabel}
        className={baseInputClass}
      />
      {popover}
      {hint && !compact && <div className="text-[10px] text-espresso-ink/50 mt-1">{hint}</div>}
    </div>
  );
}

function Field({ label, value, onChange, disabled, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full mt-1.5 px-3 py-2.5 bg-white border border-canvas-border rounded-lg text-sm brand-focus disabled:bg-canvas-border/30 disabled:text-espresso-ink/70"
      />
    </div>
  );
}

function TotalBox({ label, value, onChange, disabled, testid, accent }) {
  return (
    <div className={`rounded-xl px-4 py-3 border ${accent ? "bg-espresso text-canvas border-espresso" : "bg-canvas border-canvas-border"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${accent ? "text-canvas/70" : "text-espresso-ink/60"}`}>{label}</div>
      <NumInput
        testid={testid}
        value={value ?? 0}
        disabled={disabled}
        onChange={onChange}
        className={`w-full mt-1 bg-transparent text-lg font-bold tabular-nums outline-none border-0 p-0 disabled:opacity-70 ${accent ? "text-canvas" : "text-espresso"}`}
      />
    </div>
  );
}
